import React from 'react';
// nodejs library to set properties for components
import { styled } from '@mui/material/styles';
import Button, { type ButtonProps } from '@mui/material/Button';
import { hexToRgb } from '../../../services/styleUtils/colorUtils.js';

type KitButtonColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'twitter'
  | 'facebook'
  | 'google'
  | 'linkedin'
  | 'pinterest'
  | 'youtube'
  | 'tumblr'
  | 'github'
  | 'behance'
  | 'dribbble'
  | 'reddit'
  | 'instagram'
  | 'transparent'
  | string;

type KitButtonSize = 'sm' | 'md' | 'lg';

interface KitButtonProps extends Omit<ButtonProps, 'color' | 'size'> {
  color?: KitButtonColor;
  size?: KitButtonSize;
  simple?: boolean;
  round?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  block?: boolean;
  link?: boolean;
  justIcon?: boolean;
  fileButton?: boolean;
  className?: string;
  children?: React.ReactNode;
  target?: string | null | undefined;
}

interface KitButtonStyleProps {
  simple?: boolean;
  block?: boolean;
  link?: boolean;
  justicon?: boolean;
  socialcolor?: string;
  transparent?: boolean;
  round?: boolean;
  kitsize?: KitButtonSize;
  kitcolor?: KitButtonColor;
}

const KitButton = React.forwardRef<HTMLButtonElement, KitButtonProps>(
  (props, ref) => {
    const {
      color,
      round = false,
      children,
      fullWidth,
      disabled,
      simple = false,
      size = 'md',
      block,
      link,
      justIcon = false,
      className,
      ...rest
    } = props;

    let socialColor: string | undefined;
    let transparent = false;
    let colorParam: KitButtonColor = 'primary';

    if (color === 'primary') {
      colorParam = 'primary';
    }
    if (color === 'secondary') {
      colorParam = 'secondary';
    }
    if (color === 'info') {
      colorParam = 'info';
    }
    if (color === 'success') {
      colorParam = 'success';
    }
    if (color === 'warning') {
      colorParam = 'warning';
    }
    if (color === 'error') {
      colorParam = 'error';
    }

    if (color === 'twitter') {
      socialColor = 'twitterColor';
    }
    if (color === 'facebook') {
      socialColor = 'facebookColor';
    }
    if (color === 'google') {
      socialColor = 'googleColor';
    }
    if (color === 'linkedin') {
      socialColor = 'linkedInColor';
    }
    if (color === 'pinterest') {
      socialColor = 'pinterestColor';
    }
    if (color === 'youtube') {
      socialColor = 'youtubeColor';
    }
    if (color === 'tumblr') {
      socialColor = 'tumblrColor';
    }
    if (color === 'github') {
      socialColor = 'githubColor';
    }
    if (color === 'behance') {
      socialColor = 'behanceColor';
    }
    if (color === 'dribbble') {
      socialColor = 'dribbbleColor';
    }
    if (color === 'reddit') {
      socialColor = 'redditColor';
    }
    if (color === 'instagram') {
      socialColor = 'instagramColor';
    }
    if (color === 'transparent') {
      transparent = true;
    }

    return (
      <Container
        {...rest}
        ref={ref}
        className={className}
        kitcolor={colorParam}
        fullWidth={fullWidth}
        disabled={disabled}
        simple={simple}
        block={block}
        link={link}
        justicon={justIcon}
        socialcolor={socialColor}
        transparent={transparent}
        kitsize={size}
        round={round}
      >
        {children}
      </Container>
    );
  },
);

const Container = styled(Button, {
  shouldForwardProp: (prop) =>
    ![
      'simple',
      'block',
      'link',
      'justicon',
      'socialcolor',
      'transparent',
      'round',
      'kitsize',
      'kitcolor',
    ].includes(prop as string),
})<KitButtonStyleProps>(({ theme, kitcolor = 'primary', socialcolor }) => {
  const socialPalette = (theme.palette as Record<string, any>).social;

  return {
    minHeight: 'auto',
    minWidth: 'auto',
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    boxShadow:
      '0 2px 2px 0 rgba(' +
      hexToRgb(theme.palette.grey[500]) +
      ', 0.14), 0 3px 1px -2px rgba(' +
      hexToRgb(theme.palette.grey[500]) +
      ', 0.2), 0 1px 5px 0 rgba(' +
      hexToRgb(theme.palette.grey[500]) +
      ', 0.12)',
    border: 'none',
    borderRadius: '3px',
    position: 'relative',
    padding: '12px 30px',
    margin: '.3125rem 1px',
    fontSize: '0.875rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.02857em',
    willChange: 'box-shadow, transform',
    transition:
      'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: '1.75',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    touchAction: 'manipulation',
    cursor: 'pointer',
    '&:hover,&:focus': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.grey[500],
      boxShadow:
        '0 14px 26px -12px rgba(' +
        hexToRgb(theme.palette.grey[500]) +
        ', 0.42), 0 4px 23px 0px rgba(' +
        hexToRgb(theme.palette.common.black) +
        ', 0.12), 0 8px 10px -5px rgba(' +
        hexToRgb(theme.palette.grey[500]) +
        ', 0.2)',
    },
    '& .fab,& .fas,& .far,& .fal,& .material-icons': {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      marginTop: '-1em',
      marginBottom: '-1em',
      fontSize: '1.1rem',
      marginRight: '4px',
      verticalAlign: 'middle',
    },
    '& svg': {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      width: '18px',
      height: '18px',
      marginRight: '4px',
      verticalAlign: 'middle',
    },
    '&$justicon': {
      '& .fab,& .fas,& .far,& .fal,& .material-icons': {
        marginTop: '0px',
        marginRight: '0px',
        position: 'absolute',
        width: '100%',
        transform: 'none',
        left: '0px',
        top: '0px',
        height: '100%',
        lineHeight: '41px',
        fontSize: '20px',
      },
    },
    variants: [
      {
        props: ({ fullWidth }) => fullWidth,
        style: {
          width: '100%',
        },
      },
      {
        props: ({ kitcolor }) => kitcolor,
        style: {
          backgroundColor: theme.palette[kitcolor].main,
          boxShadow:
            '0 2px 2px 0 rgba(' +
            hexToRgb(theme.palette[kitcolor].main) +
            ', 0.14), 0 3px 1px -2px rgba(' +
            hexToRgb(theme.palette[kitcolor].main) +
            ', 0.2), 0 1px 5px 0 rgba(' +
            hexToRgb(theme.palette[kitcolor].main) +
            ', 0.12)',
          '&:hover,&:focus': {
            backgroundColor: theme.palette[kitcolor].main,
            boxShadow:
              '0 14px 26px -12px rgba(' +
              hexToRgb(theme.palette[kitcolor].main) +
              ', 0.42), 0 4px 23px 0px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.12), 0 8px 10px -5px rgba(' +
              hexToRgb(theme.palette[kitcolor].main) +
              ', 0.2)',
          },
        },
      },
      {
        props: ({ socialcolor }) => socialcolor && socialPalette,
        style: {
          backgroundColor: socialPalette?.[socialcolor || ''],
          color: theme.palette.common.white,
          boxShadow:
            '0 2px 2px 0 rgba(' +
            hexToRgb(socialPalette?.[socialcolor || '']) +
            ', 0.14), 0 3px 1px -2px rgba(' +
            hexToRgb(socialPalette?.[socialcolor || '']) +
            ', 0.2), 0 1px 5px 0 rgba(' +
            hexToRgb(socialPalette?.[socialcolor || '']) +
            ', 0.12)',
          '&:hover,&:focus,&:visited': {
            backgroundColor: socialPalette?.[socialcolor || ''],
            color: theme.palette.common.white,
            boxShadow:
              '0 14px 26px -12px rgba(' +
              hexToRgb(socialPalette?.[socialcolor || '']) +
              ', 0.42), 0 4px 23px 0px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.12), 0 8px 10px -5px rgba(' +
              hexToRgb(socialPalette?.[socialcolor || '']) +
              ', 0.2)',
          },
        },
      },
      {
        props: ({ kitcolor, simple }) => simple && kitcolor,
        style: {
          '&,&:focus,&:hover': {
            color: theme.palette.common.white,
            background: 'transparent',
            boxShadow: 'none',
          },
          '&,&:focus,&:hover,&:visited': {
            color: theme.palette[kitcolor].main,
          },
        },
      },
      {
        props: ({ socialcolor, simple }) => simple && socialcolor,
        style: {
          '&,&:focus,&:hover': {
            color: theme.palette.common.white,
            background: 'transparent',
            boxShadow: 'none',
          },
          '&,&:focus,&:hover,&:visited': {
            color: theme.palette[socialcolor || ''],
          },
        },
      },
      {
        props: ({ transparent }) => transparent,
        style: {
          '&,&:focus,&:hover': {
            color: 'inherit',
            background: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      {
        props: ({ disabled }) => disabled,
        style: {
          color: `${theme.palette.grey['50']} !important`,
          opacity: '0.65',
          pointerEvents: 'none',
        },
      },
      {
        props: {
          kitsize: 'lg',
        },
        style: {
          padding: '8px 32px',
          fontSize: '1.1375rem',
          lineHeight: '1.75',
          borderRadius: '0.2rem',
        },
      },
      {
        props: ({ justicon, kitsize }) => kitsize === 'lg' && justicon,
        style: {
          '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
            marginTop: '-4px',
          },
        },
      },
      {
        props: {
          kitsize: 'sm',
        },
        style: {
          padding: '6px 22px',
          fontSize: '0.7125rem',
          lineHeight: '1.75',
          borderRadius: '0.2rem',
        },
      },
      {
        props: ({ justicon, kitsize }) => kitsize === 'sm' && justicon,
        style: {
          '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
            marginTop: '1px',
          },
        },
      },
      {
        props: ({ round }) => round,
        style: {
          borderRadius: '30px !important',
        },
      },
      {
        props: ({ block }) => block,
        style: {
          width: '100% !important',
        },
      },
      {
        props: ({ link }) => link,
        style: {
          '&,&:hover,&:focus': {
            backgroundColor: 'transparent',
            color: theme.palette.grey[500],
            boxShadow: 'none',
          },
        },
      },
      {
        props: ({ justicon }) => justicon,
        style: {
          paddingLeft: '12px',
          paddingRight: '12px',
          fontSize: '20px',
          height: '41px',
          minWidth: '41px',
          width: '41px',
          '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
            marginRight: '0px',
          },
          '&$lg': {
            height: '57px',
            minWidth: '57px',
            width: '57px',
            lineHeight: '56px',
            '& .fab,& .fas,& .far,& .fal,& .material-icons': {
              fontSize: '32px',
              lineHeight: '56px',
            },
            '& svg': {
              width: '32px',
              height: '32px',
            },
          },
          '&$sm': {
            height: '30px',
            minWidth: '30px',
            width: '30px',
            '& .fab,& .fas,& .far,& .fal,& .material-icons': {
              fontSize: '17px',
              lineHeight: '29px',
            },
            '& svg': {
              width: '17px',
              height: '17px',
            },
          },
        },
      },
    ],
  };
});

KitButton.displayName = 'KitButton';

export default KitButton;
